'use client'

import { useIntl } from 'react-intl'

import type { CmsSellerProfile } from '@/helpers/api/fetchPayload'
import { signUpCtaLocationForTab } from '@/helpers/telemetry/signupLinkTracking'

import { hasCmsAboutBody, lexicalHasContent } from './cmsSellerProfileAbout'
import { SellerProfileLexicalRichText } from './SellerProfileLexicalRichText'
import {
  cmsSignInAccordionSections,
  SignInAboutAccordion,
  StorefrontAboutContainer,
} from './signInAboutAccordion'

/**
 * CMS About content under the sign-in prompt of a limited-visibility storefront.
 *
 * `signInAboutStyle` is the editor's choice: `blurb` renders the sign-in blurb on its own,
 * expanded, since it is written to be read there; `accordion` renders the About Tab Content
 * under an automatic "About {seller}" fold followed by one collapsed fold per section, which is
 * what Costco's CMS path always did. A profile saved before the choice existed has no style:
 * it keeps the rule it was written under, blurb when a blurb exists, else the accordion, so
 * nothing changes on deploy. With nothing written for the chosen style the prompt stands
 * alone; a "not available yet" line has no place on a sign-in prompt.
 */
export default function SignInAbout({
  profile,
  sellerName,
}: {
  profile: CmsSellerProfile
  sellerName: string
}) {
  const intl = useIntl()

  const showsBlurb =
    profile.signInAboutStyle === 'blurb' ||
    (profile.signInAboutStyle == null && lexicalHasContent(profile.signInAbout))

  if (showsBlurb) {
    if (!lexicalHasContent(profile.signInAbout)) return null
    return (
      <StorefrontAboutContainer>
        <SellerProfileLexicalRichText
          data={profile.signInAbout}
          signUpCtaLocation={signUpCtaLocationForTab(
            profile.sellerSlug,
            'about'
          )}
        />
      </StorefrontAboutContainer>
    )
  }

  if (!hasCmsAboutBody(profile)) return null

  return (
    <SignInAboutAccordion
      items={cmsSignInAccordionSections(
        profile,
        intl.formatMessage(
          { id: 'Storefront.about.signInIntroTitle' },
          { seller: sellerName }
        )
      )}
    />
  )
}
